/**
 * Google Analytics 4 Data API Service
 *
 * Fetches GA4 analytics data via the admin-ga4-report edge function.
 */

import { callEdgeFunction } from './supabase';
import type {
  GA4ReportResponse,
  GA4OverviewData,
  GA4PageData,
  GA4SourceData,
  GA4EventData,
  GA4DateRange,
} from '@/types/ga4';

/**
 * Get the full GA4 analytics report
 * @param dateRange - Number of days to look back (7, 14, 30, or 90)
 */
export async function getGA4Report(dateRange: GA4DateRange = '30'): Promise<GA4ReportResponse> {
  return callEdgeFunction<GA4ReportResponse>('admin-ga4-report', {
    dateRange,
    reportType: 'full',
  });
}

/**
 * Get overview metrics only (lighter request)
 * @param dateRange - Number of days to look back
 */
export async function getGA4Overview(dateRange: GA4DateRange = '30'): Promise<GA4OverviewData> {
  const response = await callEdgeFunction<{ overview: GA4OverviewData }>('admin-ga4-report', {
    dateRange,
    reportType: 'overview',
  });
  return response.overview;
}

/**
 * Get real-time active users count
 */
export async function getGA4RealTimeUsers(): Promise<number> {
  const response = await callEdgeFunction<{ realTimeActiveUsers: number }>('admin-ga4-report', {
    reportType: 'realtime',
  });
  return response.realTimeActiveUsers;
}

/**
 * Get top pages report
 * @param dateRange - Number of days to look back
 * @param limit - Maximum number of pages to return
 */
export async function getGA4TopPages(
  dateRange: GA4DateRange = '30',
  limit: number = 10
): Promise<GA4PageData[]> {
  const response = await callEdgeFunction<{ topPages: GA4PageData[] }>('admin-ga4-report', {
    dateRange,
    reportType: 'pages',
    limit,
  });
  return response.topPages;
}

/**
 * Get traffic sources report
 * @param dateRange - Number of days to look back
 */
export async function getGA4TrafficSources(dateRange: GA4DateRange = '30'): Promise<GA4SourceData[]> {
  const response = await callEdgeFunction<{ trafficSources: GA4SourceData[] }>('admin-ga4-report', {
    dateRange,
    reportType: 'sources',
  });
  return response.trafficSources;
}

/**
 * Get top events report
 * @param dateRange - Number of days to look back
 * @param limit - Maximum number of events to return
 */
export async function getGA4TopEvents(
  dateRange: GA4DateRange = '30',
  limit: number = 10
): Promise<GA4EventData[]> {
  const response = await callEdgeFunction<{ topEvents: GA4EventData[] }>('admin-ga4-report', {
    dateRange,
    reportType: 'events',
    limit,
  });
  return response.topEvents;
}

/**
 * Check if GA4 is configured
 * Returns true if the edge function can connect to GA4
 */
export async function checkGA4Status(): Promise<{ configured: boolean; error?: string }> {
  try {
    const response = await callEdgeFunction<{ configured: boolean; error?: string }>('admin-ga4-report', {
      reportType: 'status',
    });
    return response;
  } catch (error) {
    return {
      configured: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
